// @ts-strict-ignore
import Button from 'components/Button'

import Condition from 'components/Condition'
import ConfirmationDialog from 'components/ConfirmationDialog'
import { IUploadedVideo } from 'hooks/video'
import { useQuickCommit } from 'hooks/useQuickCommit'
import { useActionConfirmation } from 'hooks/confirmation'
import { useSnackbar } from 'hooks/redux'
import { useFetchKey } from 'hooks/relay'
import React, { useCallback } from 'react'
import { trackError } from 'services/analytics'
import { Delete } from '@mui/icons-material'
import { graphql, useLazyLoadQuery } from 'react-relay'
import { RemoveVideoButton_DeleteVideoMutation } from '__generated__/RemoveVideoButton_DeleteVideoMutation.graphql'
import { RemoveVideoButton_GetCurrentUserQuery } from '__generated__/RemoveVideoButton_GetCurrentUserQuery.graphql'
import { IconButton } from '@mui/material'

interface IRemoveVideoButtonProps {
  firm: {
    slug?: string
  }
  video: IUploadedVideo
  onVideoRemoved: () => void
  iconOnly?: boolean
}

const RemoveVideoButton = ({ firm, video, onVideoRemoved, iconOnly }: IRemoveVideoButtonProps) => {
  const [fetchKey, increaseFetchKey] = useFetchKey()
  const user = useLazyLoadQuery<RemoveVideoButton_GetCurrentUserQuery>(graphql`
    query RemoveVideoButton_GetCurrentUserQuery {
      currentUser {
        firms {
          slug
        }
      }
    }
  `, {}, {
    fetchPolicy: 'store-and-network',
    fetchKey,
  })
  const { showSnackbarMessage } = useSnackbar()

  const { execute: deleteVideoCommit } = useQuickCommit<RemoveVideoButton_DeleteVideoMutation>(
    graphql`
      mutation RemoveVideoButton_DeleteVideoMutation($input: DeleteVideoInput!) {
        deleteVideo(input: $input) {
          video {
            rawId
          }
        }
      }
    `,
  )

  const removeVideo = useCallback(async () => {
    try {
      await deleteVideoCommit({
        input: {
          videoRawId: video.rawId,
        },
      })
      if (firm?.slug === user?.currentUser?.firms?.[0]?.slug) increaseFetchKey()
      if (typeof onVideoRemoved === 'function') onVideoRemoved()
      showSnackbarMessage('The Video has been deleted', { level: 'info' })
    } catch (error) {
      showSnackbarMessage(error, { level: 'error' })
      trackError(error)
    }
  }, [deleteVideoCommit, video.rawId, firm?.slug, user?.currentUser?.firms, increaseFetchKey, onVideoRemoved, showSnackbarMessage])
  const confirmation = useActionConfirmation(removeVideo)

  if (!video || !firm?.slug || !user?.currentUser?.firms?.[0]?.slug || firm.slug !== user?.currentUser?.firms?.[0]?.slug) return null

  return (
    <React.Fragment>
      <Condition condition={iconOnly}>
        <IconButton color="primary" onClick={confirmation.perform}>
          <Delete />
        </IconButton>
      </Condition>

      <Condition condition={!iconOnly}>
        <Button color="primary" onClick={confirmation.perform} startIcon={<Delete />}>
          Remove Video
        </Button>
      </Condition>

      <ConfirmationDialog
        {...confirmation}
      />
    </React.Fragment>
  )
}

export default RemoveVideoButton
